import React, { useEffect, useRef, useState } from 'react';
import './TrainingSessionLog.css';
import Button from '@mui/material/Button';
import { ActivityInputCard } from '../components/ActivityInputCard';
import TextField from '@mui/material/TextField';
import Slider from '@mui/material/Slider';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import TextareaAutosize from '@mui/material/TextareaAutosize'
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatActivityCategory, getActivities } from '../services/activityService';
import {
  formatTrainingClassCategory,
  getTrainingClasses,
} from '../services/trainingClassService';
import { createTrainingSession } from '../services/trainingSessionService';

const steps = ['Details', 'Activities', 'Notes'];
const SUCCESS_REDIRECT_DELAY_MS = 1800;

const TrainingSessionLog = () => {
  const { accessToken, logout } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [date, setDate] = useState(null);
  const [intensity, setIntensity] = useState(5);
  const [duration, setDuration] = useState('');
  const [classTypeId, setClassTypeId] = useState('');
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState('');
  const [classTypeItems, setClassTypeItems] = useState([]);
  const [trainingClassesError, setTrainingClassesError] = useState(null);
  const [activityOptions, setActivityOptions] = useState([]);
  const [activitiesError, setActivitiesError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');

  // State to hold editable data for each activity
  const [activityDetails, setActivityDetails] = useState({});
  const navigate = useNavigate();
  const classTypeAutocompleteRef = useRef(null);
  const activitiesAutocompleteRef = useRef(null);

  const hasActivities = activities.length > 0;

  useEffect(() => {
    if (submitStatus !== 'success') {
      return undefined;
    }

    const redirectTimer = setTimeout(() => {
      navigate('/');
    }, SUCCESS_REDIRECT_DELAY_MS);

    return () => {
      clearTimeout(redirectTimer);
    };
  }, [navigate, submitStatus]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const loadCatalogs = async () => {
      try {
        const [activitiesData, trainingClassesData] = await Promise.all([
          getActivities(accessToken),
          getTrainingClasses(accessToken),
        ]);
        setActivityOptions(Array.isArray(activitiesData) ? activitiesData : []);
        setClassTypeItems(
          (Array.isArray(trainingClassesData) ? trainingClassesData : []).filter((trainingClass) => trainingClass.active)
        );
        setActivitiesError(null);
        setTrainingClassesError(null);
      } catch (error) {
        if (error?.status === 401) {
          await logout({ shouldCallApi: false });
          return;
        }
        setActivitiesError(error.message || 'Failed to load activities.');
        setTrainingClassesError(error.message || 'Failed to load training classes.');
      }
    };

    loadCatalogs();
  }, [accessToken, logout]);

  const handleNext = () => {
    setActiveStep((prev) => {
      // If we're on Details (0) and no activities selected,
      // skip Activities (1) and go straight to Notes (2)
      if (prev === 0 && !hasActivities) {
        return 2;
      }

      return Math.min(prev + 1, steps.length - 1);
    });
  };

  const handleBack = () => {
    setActiveStep((prev) => {
      // If we're on Notes and step 1 was skipped,
      // go back to Details
      if (prev === 2 && !hasActivities) {
        return 0;
      }

      return Math.max(prev - 1, 0);
    });
  };

  const sessionDurationMinutes = Number(duration) || 0;

  const totalActivityMinutes = activities.reduce((sum, a) => {
    const mins = Number(activityDetails?.[a.id]?.durationMinutes) || 0;
    return sum + mins;
  }, 0);

  const isOverDuration =
    sessionDurationMinutes > 0 && totalActivityMinutes > sessionDurationMinutes;

  const overBy = isOverDuration ? (totalActivityMinutes - sessionDurationMinutes) : 0;

  const handleActivityDetailsChange = (activityId, patch) => {
    setActivityDetails((prev) => ({
      ...prev,
      [activityId]: {
        ...(prev[activityId] || {}),
        ...patch,
      },
    }));
  };

  const handleIntensityChange = (event, newValue) => {
    setIntensity(newValue);
  };

  const handleDurationChange = (event) => {
    setDuration(event.target.value);
  };

  const handleAutocompleteFocus = (ref) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 300);
  };

  const handleSubmit = async () => {
    if (!date || !classTypeId || !duration) {
      setSubmitStatus('error');
      setSubmitError('Date, class type, and duration are required.');
      return;
    }

    if (!accessToken) {
      setSubmitStatus('error');
      setSubmitError('Missing auth token. Please sign in again.');
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);
    setSubmitStatus('loading');

    const payload = {
      sessionDate: date.toISOString(),
      sessionClassId: Number(classTypeId),
      durationMinutes: Number(duration),
      notes,
      intensity,
      activities: activities.map((activity) => ({
        activityId: activity.id,
        durationMinutes: Number(activityDetails?.[activity.id]?.durationMinutes) || 0,
        notes: activityDetails?.[activity.id]?.notes || '',
      })),
    };

    try {
      await createTrainingSession(accessToken, payload);
      setSubmitStatus('success');
    } catch (error) {
      if (error?.status === 401) {
        await logout({ shouldCallApi: false });
        return;
      }
      setSubmitStatus('error');
      setSubmitError(error.message || 'Failed to submit training session.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="training-session-log-container">
      <IconButton
        aria-label="Close"
        onClick={() => navigate('/')}
        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
      >
        <CloseIcon />
      </IconButton>
      <Stepper
        className='training-log-stepper'
        activeStep={activeStep}
        alternativeLabel
        sx={{ mb: '30px' }}
      >
        {steps.map((label, index) => {
          const isActivitiesStep = index === 1;
          const shouldDisable = isActivitiesStep && !hasActivities;

          return (
            <Step
              key={label}
              disabled={shouldDisable}
              completed={
                index < activeStep && !shouldDisable
              }
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {activeStep === 0 && (
        <>
          {activitiesError && (
            <Stack sx={{ mt: 2, mb: 2 }}>
              <Alert severity="error">{activitiesError}</Alert>
            </Stack>
          )}
          {trainingClassesError && (
            <Stack sx={{ mt: 2, mb: 2 }}>
              <Alert severity="error">{trainingClassesError}</Alert>
            </Stack>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date*"
              value={date}
              onChange={(newValue) => {
                setDate(newValue);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <div className="input-container">
            <label htmlFor="intensity-slider">Intensity: {intensity}</label>
            <Slider
              id="intensity-slider"
              value={intensity}
              onChange={handleIntensityChange}
              aria-labelledby="intensity-slider"
              valueLabelDisplay="auto"
              step={1}
              marks
              min={1}
              max={10}
            />
          </div>
          <div className="input-container" ref={classTypeAutocompleteRef}>
            <Autocomplete
              options={classTypeItems}
              groupBy={(option) => formatTrainingClassCategory(option.category)}
              getOptionLabel={(option) => option.displayName}
              value={classTypeItems.find((item) => String(item.id) === String(classTypeId)) || null}
              onFocus={() => handleAutocompleteFocus(classTypeAutocompleteRef)}
              onChange={(event, newValue) => {
                setClassTypeId(newValue ? String(newValue.id) : '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Class Type*" variant="outlined" className="autocomplete-input" />
              )}
            />
          </div>
          <div className="input-container">
            <TextField
              label="Duration (minutes)*"
              type="number"
              value={duration}
              onChange={handleDurationChange}
              inputProps={{ min: 1 }}
              fullWidth
            />
          </div>
          <div className="input-container" ref={activitiesAutocompleteRef}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              options={activityOptions}
              groupBy={(option) => formatActivityCategory(option.category)}
              getOptionLabel={(option) => option.name}
              value={activities}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onFocus={() => handleAutocompleteFocus(activitiesAutocompleteRef)}
              onChange={(event, newValue) => {
                setActivities(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Activities"
                  placeholder="Select activities"
                  className="autocomplete-input"
                />
              )}
            />
          </div>
            <div className="button-container">
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!date || !classTypeId || !duration}
              >
                Next
              </Button>
            </div>
        </>
      )}

      {activeStep === 1 && (
        <>
          {isOverDuration && (
            <Stack sx={{ mt: 2, mb: 2 }}>
              <Alert severity="warning">
                Activity durations total <b>{totalActivityMinutes}</b> min, which is <b>{overBy}</b> min over the session duration ({sessionDurationMinutes} min).
              </Alert>
            </Stack>
          )}
          <div className="activity-table">
            {activities.map((activity, index) => (
              <ActivityInputCard
                key={activity.id || index}
                name={activity.name}
                notes={activityDetails[activity.id]?.notes || ''}
                durationMinutes={activityDetails[activity.id]?.durationMinutes || 0}
                maxDuration={sessionDurationMinutes || 120}
                onChange={(patch) => handleActivityDetailsChange(activity.id, patch)}
                onRemove={() => {
                  const newActivities = activities.filter((_, i) => i !== index);
                  setActivities(newActivities);

                  setActivityDetails((prev) => {
                    const next = { ...prev };
                    delete next[activity.id];
                    return next;
                  });
                }}
              />
            ))}
          </div>
          <div className="new-training-button-group">
            <Button variant="outlined" onClick={handleBack}>Back</Button>
            <Button variant="contained" onClick={handleNext}>Next</Button>
          </div>
        </>
      )}
      {activeStep === 2 && (
        <div>
          <h2>Page 3</h2>
         {submitStatus === 'loading' && (
           <Stack sx={{ mt: 2, mb: 2 }}>
             <Alert severity="info">Submitting training log...</Alert>
           </Stack>
         )}
         {submitStatus === 'success' && (
           <Stack sx={{ mt: 2, mb: 2 }}>
             <Alert severity="success">Training log submitted. Returning to home...</Alert>
           </Stack>
         )}
         {submitStatus === 'error' && submitError && (
           <Stack sx={{ mt: 2, mb: 2 }}>
             <Alert severity="error">{submitError}</Alert>
           </Stack>
         )}
         <div className="input-container">
             <TextareaAutosize
               aria-label="Notes from Training"
               minRows={4}
               placeholder="Optional Notes From Training"
               className="notes-textarea"
               value={notes}
               onChange={(e) => setNotes(e.target.value)}
             />
          </div>
           <div className='new-training-button-group'>
              <Button variant="outlined" onClick={handleBack} disabled={isSubmitting || submitStatus === 'success'}>Back</Button>
              <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting || submitStatus === 'success'}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
           </div>
        </div>
      )}
    </div>
  );
};

export default TrainingSessionLog;
